/**
 * Mercier Points: the Smile bridge (docs/HANDOFF.md section 3).
 *
 * Smile's Shopify app embed loads Smile UI (the panel) on every page. This file connects the theme
 * to it with Smile's documented JavaScript API only (dev.smile.io):
 *
 * 1. Anything with data-mercier-smile-open="<deep link>" opens Smile's panel at that page
 *    (SmileUI.openPanel): home, points_activity_rules, points_products, referral_program_details.
 *    When Smile UI isn't on the page the click falls through to the element's own href, so nothing
 *    breaks. The nav coin does the same in mercier-header.js.
 *
 * 2. Signed in, it reads the customer's balance with Smile.js (Smile.customerPointsWallet.get())
 *    and fires `mercier:points` on document, which fills the Account page card (mercier-pages.js).
 *    Smile.js is only on the page while the JavaScript SDK is on in Smile Admin > Settings >
 *    Developer Tools. Without it the card keeps its earning-rate text.
 *
 * Reward maths (points per reward, reward value) comes from Theme settings > Mercier Points through
 * the data attributes on the script tag (snippets/mercier-smile.liquid).
 */

const config = (() => {
  const el = document.currentScript || document.querySelector('script[data-mercier-smile]');
  const data = el instanceof HTMLElement ? el.dataset : {};
  return {
    per: Math.max(1, parseInt(data.per || '100', 10) || 100),
    value: parseFloat(data.value || '5') || 5,
    customer: data.customer !== undefined,
  };
})();

/* ---------- Open Smile's panel ---------- */

/** Smile UI, once its embed has loaded it (null until then, or when the embed is off). */
function smileUI() {
  const ui = /** @type {any} */ (window).SmileUI;
  return ui && typeof ui.openPanel === 'function' ? ui : null;
}

/**
 * Opens Smile's panel at a deep link. Returns false when Smile UI isn't on the page, so the caller
 * can fall back to its own behaviour.
 * @param {string} [deepLink]
 */
function openSmilePanel(deepLink = 'home') {
  const ui = smileUI();
  if (!ui) return false;
  const open = () => ui.openPanel({ deep_link: deepLink });
  // ready() resolves once the panel is initialised (immediately if it already is)
  if (typeof ui.ready === 'function') ui.ready().then(open, open);
  else open();
  return true;
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-mercier-smile-open]') : null;
  if (!(target instanceof HTMLElement)) return;
  if (openSmilePanel(target.dataset.mercierSmileOpen || 'home')) event.preventDefault();
});

/* ---------- Balance for the Account page card ---------- */

if (config.customer) {
  let published = false;

  /** @param {any} wallet */
  const readBalance = (wallet) => {
    if (typeof wallet === 'number') return wallet;
    if (!wallet || typeof wallet !== 'object') return null;
    for (const key of ['pointsBalance', 'points_balance', 'balance']) {
      if (wallet[key] === undefined || wallet[key] === null) continue;
      const n = Number(wallet[key]);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };

  /** @param {any} wallet */
  const publish = (wallet) => {
    const balance = readBalance(wallet);
    if (balance === null || published) return;
    published = true;
    const into = balance % config.per;
    const detail = {
      balance,
      per: config.per,
      value: config.value,
      left: config.per - into,
      progress: Math.round((into / config.per) * 100),
    };
    /** @type {any} */ (window).MercierPoints = detail;
    document.dispatchEvent(new CustomEvent('mercier:points', { detail }));
  };

  const fetchWallet = () => {
    const smile = /** @type {any} */ (window).Smile;
    if (!smile || !smile.customerPointsWallet || typeof smile.customerPointsWallet.get !== 'function') return;
    try {
      // Rejects until Smile.js has initialised; the smile-js-initialised listener below covers that.
      smile.customerPointsWallet.get().then(publish, () => {});
    } catch (error) {
      // Not initialised yet: wait for the event.
    }
  };

  document.addEventListener('smile-js-initialized', fetchWallet);
  fetchWallet();
}
